import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiConfig } from 'const/apiConfig.const';
import { useUserData } from 'core/state/useUserData';
import { useOrgRoleData } from 'core/state/useOrgRoleData';
import { useOrgSetupData } from 'core/state/useOrgSetupData';
import { useFetchWithAuth } from '../auth/fetchWithAuth';
import { Loading } from 'shared/components/LoadingComponent';
import { useWebsocketConnection } from 'core/state/useWebsocketConnection';
import { useWixSiteIdForOrgMapping } from 'core/state/useWixSiteIdForOrgMapping';
import { refreshUserData } from 'core/utils/refreshUserData';
import { getOrgSubscriptionAction } from 'core/utils/subscriptionStatus';
import { trackFunnelEvent } from 'core/analytics/funnelEvents';

export const OrganizationAccessGuard = ({ component: Component, ...props }) => {
  const navigate = useNavigate();
  const { userData, updateUserData } = useUserData();
  const [orgRoleData, updateOrgRoleData, removeOrgRoleData] = useOrgRoleData();
  const [orgSetupData, updateOrgSetupData, removeOrgSetupData] =
    useOrgSetupData();
  const [
    websocketConnection,
    updateWebsocketConnection,
    removeWebsocketConnection,
  ] = useWebsocketConnection();
  const location = useLocation();
  const { getData } = useFetchWithAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const setAccessAndNavigate = (access, path, reason?: string) => {
    setHasAccess(access);
    if (!access) {
      if (reason) {
        trackFunnelEvent('org_guard_redirect', {
          reason,
          path,
          from: location.pathname,
        });
      }
      navigate(path);
    }
  };
  const [wixSiteIdForOrgMapping] = useWixSiteIdForOrgMapping();

  const isWebsocketConnectionExpired = (connection) => {
    if (!connection || !connection.timestamp) return true;
    const TWO_HOURS = 2 * 60 * 60 * 1000; // 2 hours in ms
    return Date.now() - connection.timestamp > TWO_HOURS;
  };

  const fetchWebsocketConnection = async () => {
    try {
      const response = await getData(
        apiConfig.api.websocketConnectionUrl,
        {
          method: 'GET',
        },
        true,
      );
      if (!response.ok) {
        console.warn('Unable to fetch websocket connection URL');
        return;
      }
      const responseData = await response.text();
      updateWebsocketConnection(responseData);
    } catch (e) {
      console.warn('Unable to fetch websocket connection URL', e);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let responseData = userData;

        const shouldRefreshUserData =
          !userData || location.pathname === '/selectOrganization';

        // Fetch User Data if not available or when org list needs live subscription status
        if (shouldRefreshUserData) {
          setLoading(true);
          const refreshed = await refreshUserData({
            getData,
            updateUserData,
            updateOrgRoleData,
            currentOrgId: orgRoleData?.org?.id,
          });
          if (refreshed) {
            responseData = refreshed;
          } else if (!userData) {
            const response = await getData(apiConfig.api.user, { method: 'GET' });
            responseData = await response.json();
            updateUserData(responseData);
          }
          setLoading(false);
        }

        // Fetch Org Setup Data
        if (orgRoleData && !orgSetupData) {
          const fetchSetupData = async () => {
            try {
              const response = await getData(apiConfig.api.orgSetupDetails, {
                method: 'GET',
              });
              const responseData = await response.json();
              updateOrgSetupData(responseData);
            } catch (e) {
              setLoading(false);
              setHasAccess(false);
              console.error(e);
            }
          };
          fetchSetupData();
        }

        // Always check if the email is verified, regardless of props.verifyAll
        if (!responseData?.auth0UserInfo?.email_verified) {
          setAccessAndNavigate(false, '/registration/verify-email', 'email_unverified');
          return;
        }

        // If props.verifyAll is false, after checking email verification, grant access and return
        if (!props.verifyAll) {
          setHasAccess(true);
          return;
        }

        // Check if the user first name and last name is available
        // if (!responseData?.firstName || !responseData?.lastName) {
        //   setAccessAndNavigate(false, '/registration/user-profile');
        //   return;
        // }

        // Is Not Same User as previously logged in
        if (orgRoleData && responseData.id !== orgRoleData?.id?.userId) {
          removeOrgRoleData();
          setAccessAndNavigate(false, '/selectOrganization', 'user_mismatch');
          return;
        }

        const hasAtleastOneOrgAssociation =
          responseData?.userOrgRoles?.length > 0;
        const subscriptionAction = getOrgSubscriptionAction(orgRoleData?.org);
        const hasSubscription = subscriptionAction === 'allow';

        // Has atleast 1 organization
        if (!hasAtleastOneOrgAssociation) {
          // // Coming from Wix, go straight to org registration (avoid Join Org confusion)
          // if (wixSiteIdForOrgMapping) {
          //   setAccessAndNavigate(false, '/registration/organization');
          // } else {
          //   // setAccessAndNavigate(false, '/registration');
          //   // Go straight to organization registration for non wix users
          //   setAccessAndNavigate(false, '/registration/organization');
          // }
          setAccessAndNavigate(false, '/registration/organization', 'no_org');

          return;
        }
        // Has one org Association and the org has not been selected yet but has active subscription
        if (responseData?.userOrgRoles?.length === 1 && !orgRoleData) {
          const soleOrgRole = responseData.userOrgRoles[0];
          const soleOrgAction = getOrgSubscriptionAction(soleOrgRole?.org);

          if (soleOrgAction === 'allow') {
            updateOrgRoleData(soleOrgRole);
            setHasAccess(true);

            // Fetch WebSocket connection on first-time setup
            await fetchWebsocketConnection();

            return;
          }

          if (soleOrgAction === 'select_plan') {
            updateOrgRoleData(soleOrgRole);
            setAccessAndNavigate(
              location.pathname === '/registration/subscription',
              '/registration/subscription',
              'missing_subscription',
            );
            return;
          }

          removeOrgRoleData();
          setAccessAndNavigate(false, '/selectOrganization', 'select_organization');
          return;
        }

        // Has multipe organizations and the org has not been selected yet
        if (
          responseData.userOrgRoles.length > 1 &&
          location.pathname !== '/selectOrganization' &&
          !orgRoleData
        ) {
          setAccessAndNavigate(false, '/selectOrganization', 'select_organization');
          return;
        }
        // Org has been selected and Has Organization Subscription
        if (hasSubscription) {
          updateOrgRoleData(orgRoleData);
          setHasAccess(true);

          // Also fetch WebSocket connection if missing or expired
          if (
            !websocketConnection ||
            isWebsocketConnectionExpired(websocketConnection)
          ) {
            await fetchWebsocketConnection();
          }
        } else {
          if (subscriptionAction === 'select_plan') {
            setAccessAndNavigate(
              location.pathname === '/registration/subscription',
              '/registration/subscription',
              'missing_subscription',
            );
          } else {
            removeOrgRoleData();
            setAccessAndNavigate(false, '/selectOrganization', 'subscription_expired');
          }
        }
      } catch (e) {
        setLoading(false);
        setHasAccess(false);
        console.error(e);
      }
    };
    fetchData();
  }, []);

  if (loading)
    return <Loading message="Loading your organizational settings..." />;

  if (hasAccess === null) return null; // Loading or initial state
  if (hasAccess) return <Component />;
  return null;
};
